import { useTranslation } from "react-i18next";
import React, { useCallback } from "react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { QuantityInput } from "@/components/ui/quantity-input";
import { AlertCircle, Trash2, Clock } from "lucide-react";
import { Product, ProductSelection } from "@/types/product";
import { ProductThumbnail } from "@/components/products/ProductThumbnail";
import {
  multiplyMoney,
  formatCentsAsDollars,
  formatUnitOfMeasure,
} from "@/lib/price-utils";


interface SelectedProductItemProps {
  selectedProduct: ProductSelection;
  product: Product; // Full product details
  onQuantityChange: (quantity: number) => void;
  onRemove: () => void;
  showPricing?: boolean;
  disabled?: boolean;
  /** Hides remove button and qty stepper, shows static qty instead. */
  hideControls?: boolean;
  className?: string;
}

// Helper to check if a product has pricing information
const hasUnitPrice = (
  product: ProductSelection,
): product is ProductSelection & { unitPrice: number } => {
  return "unitPrice" in product && typeof product.unitPrice === "number";
};

export const SelectedProductItem: React.FC<SelectedProductItemProps> = ({
  selectedProduct,
  product,
  onQuantityChange,
  onRemove,
  showPricing = false,
  disabled = false,
  hideControls = false,
  className,
}) => {
  const { t } = useTranslation();
  const handleQuantityChange = useCallback(
    (newQuantity: number) => {
      onQuantityChange(newQuantity);
    },
    [onQuantityChange],
  );

  const handleRemove = useCallback(() => {
    onRemove();
  }, [onRemove]);

  // Get quantity from the standard QuoteProduct.quantity property
  const quantity = selectedProduct.quantity;
  const unitPrice = hasUnitPrice(selectedProduct)
    ? selectedProduct.unitPrice
    : undefined;

  // Determine if this is a requested item based on product availability
  const isOutOfStock = product.currentAvailability === "out_of_stock";
  const isSpecialOrder = product.currentAvailability === "special_order";
  const isRequestedItem = isOutOfStock || isSpecialOrder;

  return (
    <div className={`p-3 border rounded-lg ${className || ""}`}>
      {/* Product Info Row */}
      <div className="flex items-start gap-3 mb-3">
        <ProductThumbnail
          imageUrl={product.imageUrl}
          alt={product.description}
          size="sm"
        />

        <div className="flex-1 min-w-0 pr-2">
          <h4 className="font-medium text-sm line-clamp-2 mb-1">
            {product.description}
          </h4>
          <div className="flex items-center gap-2 flex-wrap">
            <div className="text-xs text-muted-foreground">
              {t("common.id")} {product.hajocaProductId}
            </div>
            {isRequestedItem && (
              <Badge
                variant="secondary"
                className="text-[10px] px-1.5 py-0.5 bg-orange-100 text-orange-700 hover:bg-orange-100 hover:text-orange-700"
              >
                {isOutOfStock ? (
                  <>
                    <AlertCircle className="h-3 w-3 mr-1" />
                    {t("components.products.selectedProductItem.requested")}
                  </>
                ) : (
                  <>
                    <Clock className="h-3 w-3 mr-1" />
                    {t("components.products.selectedProductItem.specialOrder")}
                  </>
                )}
              </Badge>
            )}
            {!isRequestedItem &&
              product.currentQuantityAvailable != null &&
              quantity > product.currentQuantityAvailable && (
                <Badge
                  variant="secondary"
                  className="text-[10px] px-1.5 py-0.5 bg-orange-100 text-orange-700 hover:bg-orange-100 hover:text-orange-700"
                >
                  <AlertCircle className="h-3 w-3 mr-1" />
                  {t("components.products.selectedProductItem.requested")}
                </Badge>
              )}
          </div>
        </div>

        {!hideControls && (
          <Button
            type="button"
            variant="ghost"
            size="sm"
            onClick={handleRemove}
            disabled={disabled}
            className="text-muted-foreground hover:text-destructive p-1 h-6 w-6 flex-shrink-0"
          >
            <Trash2 className="h-3 w-3" />
          </Button>
        )}
      </div>

      {/* Quantity and Price Row */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <span className="text-sm text-muted-foreground">{t("components.products.selectedProductItem.qty")}</span>
          {hideControls ? (
            <span className="text-sm font-medium">{quantity}</span>
          ) : (
            <QuantityInput
              value={quantity}
              onChange={handleQuantityChange}
              min={1}
              max={9999}
              size="sm"
              disabled={disabled}
            />
          )}
        </div>

        {showPricing && unitPrice ? (
          <div className="text-right">
            <div className="text-xs text-muted-foreground">
              ${formatCentsAsDollars(unitPrice)}{" "}
              {formatUnitOfMeasure(product.unitOfMeasure)}
            </div>
            <div className="text-sm font-semibold text-primary">
              ${formatCentsAsDollars(multiplyMoney(unitPrice, quantity))}
            </div>
          </div>
        ) : showPricing ? (
          <div className="flex items-center gap-1 text-muted-foreground">
            <AlertCircle className="h-3 w-3" />
            <span className="text-xs">{t("components.products.selectedProductItem.priceTbd")}</span>
          </div>
        ) : null}
      </div>

      {/* Requesting more than available message */}
      {product.currentQuantityAvailable != null &&
        quantity > product.currentQuantityAvailable && (
          <div className="mt-2 text-xs text-muted-foreground text-center">
            {t("components.products.selectedProductItem.youAreRequestingMore")}
          </div>
        )}

      {/* Notes for quote products */}
      {selectedProduct.notes && (
        <div className="mt-2 pt-2 border-t">
          <div className="text-xs text-muted-foreground mb-1">{t("components.products.selectedProductItem.notes")}</div>
          <div className="text-xs">{selectedProduct.notes}</div>
        </div>
      )}
    </div>
  );
};
